import { useMemo } from 'react';
import { Form, Select } from 'antd';
import { ROLES_PERMISSIONS } from '@/constants';
import { useEnforceAccessControl } from '@/hooks';
import { cn } from '@/libs/utils';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';
import CompanyUserChip from './CompanyUserChip';

/** Clients, officers and company admins assigned to the company. */
export default function CompanyAccessAssignments() {
  const {
    adminsData,
    clientOfficersData,
    clientsAdminsClientOfficersKeys,
    clientsAdminsClientOfficersState,
    clientsAdminsClientState,
    clientsAdminsClientsKeys,
    clientsAdminsCompanyAdminsKeys,
    clientsAdminsCompanyAdminsState,
    clientsAdminsEInvoiceOfficersKeys,
    clientsAdminsEInvoiceOfficersState,
    clientsData,
    eInvoiceChecked,
    eInvoiceOfficersData,
    formData,
    isProfile,
    sectionClass,
    t,
    usersIsLoading,
  } = useCompanyGeneralDataForm();

  // Who may open user accounts sees a person's details on their card.
  const canOpenUsers = useEnforceAccessControl(ROLES_PERMISSIONS.USERS.VIEW);
  const usersById = useMemo(() => {
    const byId = {};
    Object.values(formData?.clientsAdmins || {}).forEach((list) => {
      (Array.isArray(list) ? list : []).forEach((user) => {
        if (user?._id) byId[String(user._id)] = user;
      });
    });
    return byId;
  }, [formData?.clientsAdmins]);
  const tagRender = (props) => (
    <CompanyUserChip
      {...props}
      user={usersById[String(props.value)]}
      withDetails={canOpenUsers}
      t={t}
    />
  );

  return (
    <div
      className={cn(
        'grid gap-x-5 rounded-md border bg-zinc-50 px-4 pt-5',
        // On the profile each list takes the full width, so its people sit
        // side by side instead of one under the other.
        isProfile ? 'grid-cols-1' : 'grid-cols-2',
        sectionClass('pristup')
      )}
    >
      {clientsAdminsClientState?.visible && (
        <Form.Item
          name={clientsAdminsClientsKeys}
          label={t('clients', { ns: 'forms' })}
        >
          <Select
            allowClear
            showSearch
            mode="multiple"
            tagRender={tagRender}
            placeholder={t('clients', { ns: 'forms' })}
            className="w-full"
            loading={usersIsLoading}
            options={clientsData}
            optionFilterProp="label"
            disabled={clientsAdminsClientState?.disabled}
          />
        </Form.Item>
      )}

      {clientsAdminsClientOfficersState?.visible && (
        <Form.Item
          name={clientsAdminsClientOfficersKeys}
          label={t('clientOfficers', { ns: 'forms' })}
        >
          <Select
            showSearch
            allowClear
            mode="multiple"
            tagRender={tagRender}
            placeholder={t('clientOfficers', { ns: 'forms' })}
            className="w-full"
            loading={usersIsLoading}
            options={clientOfficersData}
            optionFilterProp="label"
            disabled={clientsAdminsClientOfficersState?.disabled}
          />
        </Form.Item>
      )}

      {eInvoiceChecked && clientsAdminsEInvoiceOfficersState?.visible && (
        <Form.Item
          name={clientsAdminsEInvoiceOfficersKeys}
          label={t('eInvoiceOfficers', { ns: 'forms' })}
        >
          <Select
            allowClear
            showSearch
            mode="multiple"
            tagRender={tagRender}
            placeholder={t('eInvoiceOfficers', { ns: 'forms' })}
            className="w-full"
            loading={usersIsLoading}
            options={eInvoiceOfficersData}
            optionFilterProp="label"
            disabled={clientsAdminsEInvoiceOfficersState?.disabled}
          />
        </Form.Item>
      )}

      {clientsAdminsCompanyAdminsState?.visible && (
        <Form.Item
          name={clientsAdminsCompanyAdminsKeys}
          label={t('companyAdministrators', { ns: 'forms' })}
        >
          <Select
            allowClear
            showSearch
            mode="multiple"
            tagRender={tagRender}
            placeholder={t('companyAdministrators', { ns: 'forms' })}
            className="w-full"
            loading={usersIsLoading}
            options={adminsData}
            optionFilterProp="label"
            disabled={clientsAdminsCompanyAdminsState?.disabled}
          />
        </Form.Item>
      )}
    </div>
  );
}
